import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createBlock as _createBlock, normalizeClass as _normalizeClass, vModelText as _vModelText, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "member-stamp-panel" }
const _hoisted_2 = { class: "stamp-modal-card" }
const _hoisted_3 = { class: "stamp-modal-header" }
const _hoisted_4 = { class: "header-icon" }
const _hoisted_5 = { class: "stamp-modal-body" }
const _hoisted_6 = { class: "stamp-grid" }
const _hoisted_7 = ["onClick"]
const _hoisted_8 = ["src", "alt"]
const _hoisted_9 = {
  key: 1,
  class: "stamp-empty"
}
const _hoisted_10 = {
  key: 2,
  class: "stamp-overlay"
}
const _hoisted_11 = { class: "stamp-label" }
const _hoisted_12 = { class: "stamp-actions" }
const _hoisted_13 = { class: "stamp-names" }
const _hoisted_14 = { class: "names-grid" }
const _hoisted_15 = { class: "name-field" }
const _hoisted_16 = { class: "name-field" }
const _hoisted_17 = { class: "name-field" }
const _hoisted_18 = { class: "name-field" }
const _hoisted_19 = { class: "stamp-modal-footer" }

import { ref, reactive, onBeforeUnmount } from 'vue';
import { bankbookApi } from '@/services/api';
import LoadingOverlay from '@/components/common/LoadingOverlay.vue';

type StampTypeKey = typeof stampTypes[number]['key'];


export default /*@__PURE__*/_defineComponent({
  __name: 'MemberStampPanel',
  props: {
    memberCode: {}
  },
  setup(__props: any) {

const props = __props;

const stampTypes = [
  { key: 'chief', label: '시설장' },
  { key: 'paym', label: '담당자' },
  { key: 'income', label: '수입원' },
  { key: 'outcome', label: '지출원' },
] as const;

const showModal = ref(false);
const loading = ref(false);
const saving = ref(false);
const uploading = ref(false);
const deletingType = ref('');
const currentStampType = ref<StampTypeKey | ''>('');
const fileInputRef = ref<HTMLInputElement | null>(null);

const memberInfo = reactive({
  chief_name: '',
  paym_name: '',
  income_name: '',
  outcome_name: '',
});

const stampImages = ref<Record<string, string>>({});
const stampBlobUrls: string[] = [];

const revokeStampUrls = () => {
  stampBlobUrls.forEach((url) => URL.revokeObjectURL(url));
  stampBlobUrls.length = 0;
  stampImages.value = {};
};

const loadStampImage = async (stampType: StampTypeKey) => {
  try {
    const response = await bankbookApi.getStampImage(props.memberCode, stampType);
    const url = URL.createObjectURL(response.data);
    stampBlobUrls.push(url);
    stampImages.value[stampType] = url;
  } catch {
    delete stampImages.value[stampType];
  }
};

const loadAllData = async () => {
  if (!props.memberCode) return;
  loading.value = true;
  revokeStampUrls();

  try {
    const financeRes = await bankbookApi.getFinanceInfo(props.memberCode);
    const data = financeRes.data?.data;
    if (data) {
      memberInfo.chief_name = data.chief_name || '';
      memberInfo.paym_name = data.paym_name || '';
      memberInfo.income_name = data.income_name || '';
      memberInfo.outcome_name = data.outcome_name || '';
    }

    const listRes = await bankbookApi.getStampList(props.memberCode);
    const stamps = listRes.data?.data || {};
    const types = Object.keys(stamps).filter((k) => stamps[k]);
    await Promise.all(types.map((type) => loadStampImage(type as StampTypeKey)));
  } catch (error: any) {
    const message = error.response?.data?.message || error.message || '알 수 없는 오류';
    alert(`결재란 정보 불러오기 실패: ${message}`);
  } finally {
    loading.value = false;
  }
};

const openModal = async () => {
  showModal.value = true;
  await loadAllData();
};

const closeModal = () => {
  showModal.value = false;
};

const openFileSelector = (stampType: StampTypeKey) => {
  currentStampType.value = stampType;
  fileInputRef.value?.click();
};

const onFileSelected = async (event: Event) => {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  input.value = '';
  if (!file || !currentStampType.value) return;

  uploading.value = true;
  try {
    await bankbookApi.uploadStamp(props.memberCode, currentStampType.value, file);
    if (stampImages.value[currentStampType.value]) {
      URL.revokeObjectURL(stampImages.value[currentStampType.value]);
      const idx = stampBlobUrls.indexOf(stampImages.value[currentStampType.value]);
      if (idx >= 0) stampBlobUrls.splice(idx, 1);
    }
    await loadStampImage(currentStampType.value);
  } catch (error: any) {
    const message = error.response?.data?.message || error.message || '알 수 없는 오류';
    alert(`도장 업로드 실패: ${message}`);
  } finally {
    uploading.value = false;
    currentStampType.value = '';
  }
};

const handleDeleteStamp = async (stampType: StampTypeKey) => {
  if (!confirm(`${stampTypes.find((t) => t.key === stampType)?.label} 도장을 삭제하시겠습니까?`)) return;

  deletingType.value = stampType;
  try {
    await bankbookApi.deleteStamp(props.memberCode, stampType);
    if (stampImages.value[stampType]) {
      URL.revokeObjectURL(stampImages.value[stampType]);
      const idx = stampBlobUrls.indexOf(stampImages.value[stampType]);
      if (idx >= 0) stampBlobUrls.splice(idx, 1);
      delete stampImages.value[stampType];
    }
  } catch (error: any) {
    const message = error.response?.data?.message || error.message || '알 수 없는 오류';
    alert(`도장 삭제 실패: ${message}`);
  } finally {
    deletingType.value = '';
  }
};

const saveMemberInfo = async () => {
  saving.value = true;
  try {
    await bankbookApi.upsertFinanceInfo(props.memberCode, { ...memberInfo });
    alert('결재자 정보가 저장되었습니다.');
    closeModal();
  } catch (error: any) {
    const message = error.response?.data?.message || error.message || '알 수 없는 오류';
    alert(`결재자 정보 저장 실패: ${message}`);
  } finally {
    saving.value = false;
  }
};

onBeforeUnmount(() => {
  revokeStampUrls();
});

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_dialog = _resolveComponent("v-dialog")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("button", {
      type: "button",
      class: "stamp-open-btn",
      onClick: openModal
    }, [
      _createVNode(_component_v_icon, { size: "14" }, {
        default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
          _createTextVNode("mdi-stamper", -1)
        ]))]),
        _: 1
      }),
      _cache[6] || (_cache[6] = _createTextVNode(" 결재란정보 ", -1))
    ]),
    _createVNode(_component_v_dialog, {
      modelValue: showModal.value,
      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((showModal).value = $event)),
      "max-width": "900",
      persistent: "",
      "content-class": "member-stamp-dialog"
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(LoadingOverlay, {
            show: loading.value,
            text: "불러오는 중..."
          }, null, 8, ["show"]),
          _createVNode(LoadingOverlay, {
            show: saving.value,
            text: "저장 중...",
            variant: "save"
          }, null, 8, ["show"]),
          _createVNode(LoadingOverlay, {
            show: uploading.value,
            text: "업로드 중..."
          }, null, 8, ["show"]),
          _createVNode(LoadingOverlay, {
            show: deletingType.value !== '',
            text: "삭제 중...",
            variant: "delete"
          }, null, 8, ["show"]),
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createVNode(_component_v_icon, {
                size: "20",
                color: "white"
              }, {
                default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                  _createTextVNode("mdi-stamper", -1)
                ]))]),
                _: 1
              })
            ]),
            _cache[9] || (_cache[9] = _createElementVNode("div", { class: "header-text" }, [
              _createElementVNode("h3", null, "결재란 정보"),
              _createElementVNode("p", null, "결의서 인명 · 도장 이미지")
            ], -1)),
            _cache[10] || (_cache[10] = _createElementVNode("div", { class: "header-spacer" }, null, -1)),
            _createElementVNode("button", {
              type: "button",
              class: "close-btn",
              onClick: closeModal
            }, [
              _createVNode(_component_v_icon, { size: "20" }, {
                default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                  _createTextVNode("mdi-close", -1)
                ]))]),
                _: 1
              })
            ])
          ]),
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("div", _hoisted_6, [
              (_openBlock(), _createElementBlock(_Fragment, null, _renderList(stampTypes, (stampType) => {
                return _createElementVNode("div", {
                  key: stampType.key,
                  class: _normalizeClass(["stamp-card", { 'has-image': stampImages.value[stampType.key] }])
                }, [
                  _createElementVNode("div", {
                    class: "stamp-image-area",
                    onClick: ($event: any) => (openFileSelector(stampType.key))
                  }, [
                    (stampImages.value[stampType.key])
                      ? (_openBlock(), _createElementBlock("img", {
                          key: 0,
                          src: stampImages.value[stampType.key],
                          alt: stampType.label,
                          class: "stamp-img"
                        }, null, 8, _hoisted_8))
                      : (_openBlock(), _createElementBlock("div", _hoisted_9, [
                          _createVNode(_component_v_icon, {
                            size: "32",
                            color: "#94a3b8"
                          }, {
                            default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                              _createTextVNode("mdi-stamper", -1)
                            ]))]),
                            _: 1
                          }),
                          _createElementVNode("span", null, _toDisplayString(stampType.label), 1)
                        ])),
                    (stampImages.value[stampType.key])
                      ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                          _createVNode(_component_v_icon, {
                            size: "20",
                            color: "white"
                          }, {
                            default: _withCtx(() => [...(_cache[12] || (_cache[12] = [
                              _createTextVNode("mdi-pencil", -1)
                            ]))]),
                            _: 1
                          })
                        ]))
                      : _createCommentVNode("", true)
                  ], 8, _hoisted_7),
                  _createElementVNode("div", _hoisted_11, _toDisplayString(stampType.label), 1),
                  _createElementVNode("div", _hoisted_12, [
                    (stampImages.value[stampType.key])
                      ? (_openBlock(), _createBlock(_component_v_btn, {
                          key: 0,
                          variant: "flat",
                          class: "btn-delete",
                          size: "small",
                          block: "",
                          disabled: deletingType.value === stampType.key,
                          onClick: ($event: any) => (handleDeleteStamp(stampType.key))
                        }, {
                          default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
                            _createTextVNode(" 삭제 ", -1)
                          ]))]),
                          _: 1
                        }, 8, ["disabled", "onClick"]))
                      : (_openBlock(), _createBlock(_component_v_btn, {
                          key: 1,
                          variant: "flat",
                          class: "btn-confirm",
                          size: "small",
                          block: "",
                          onClick: ($event: any) => (openFileSelector(stampType.key))
                        }, {
                          default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
                            _createTextVNode(" 추가 ", -1)
                          ]))]),
                          _: 1
                        }, 8, ["onClick"]))
                  ])
                ], 2)
              }), 64))
            ]),
            _createElementVNode("div", _hoisted_13, [
              _cache[19] || (_cache[19] = _createElementVNode("div", { class: "names-title" }, "결재자 이름", -1)),
              _createElementVNode("div", _hoisted_14, [
                _createElementVNode("div", _hoisted_15, [
                  _cache[15] || (_cache[15] = _createElementVNode("label", null, "시설장", -1)),
                  _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((memberInfo.chief_name) = $event)),
                    type: "text",
                    placeholder: "시설장"
                  }, null, 512), [
                    [_vModelText, memberInfo.chief_name]
                  ])
                ]),
                _createElementVNode("div", _hoisted_16, [
                  _cache[16] || (_cache[16] = _createElementVNode("label", null, "담당자", -1)),
                  _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((memberInfo.paym_name) = $event)),
                    type: "text",
                    placeholder: "담당자"
                  }, null, 512), [
                    [_vModelText, memberInfo.paym_name]
                  ])
                ]),
                _createElementVNode("div", _hoisted_17, [
                  _cache[17] || (_cache[17] = _createElementVNode("label", null, "수입원", -1)),
                  _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((memberInfo.income_name) = $event)),
                    type: "text",
                    placeholder: "수입원"
                  }, null, 512), [
                    [_vModelText, memberInfo.income_name]
                  ])
                ]),
                _createElementVNode("div", _hoisted_18, [
                  _cache[18] || (_cache[18] = _createElementVNode("label", null, "지출원", -1)),
                  _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((memberInfo.outcome_name) = $event)),
                    type: "text",
                    placeholder: "지출원"
                  }, null, 512), [
                    [_vModelText, memberInfo.outcome_name]
                  ])
                ])
              ])
            ])
          ]),
          _createElementVNode("div", _hoisted_19, [
            _createVNode(_component_v_btn, {
              variant: "text",
              class: "btn-cancel",
              onClick: closeModal
            }, {
              default: _withCtx(() => [...(_cache[20] || (_cache[20] = [
                _createTextVNode("취소", -1)
              ]))]),
              _: 1
            }),
            _createVNode(_component_v_spacer),
            _createVNode(_component_v_btn, {
              variant: "flat",
              class: "btn-save",
              disabled: saving.value,
              onClick: saveMemberInfo
            }, {
              default: _withCtx(() => [
                (saving.value)
                  ? (_openBlock(), _createBlock(_component_v_icon, {
                      key: 0,
                      class: "mdi-spin",
                      size: "16"
                    }, {
                      default: _withCtx(() => [...(_cache[21] || (_cache[21] = [
                        _createTextVNode("mdi-loading", -1)
                      ]))]),
                      _: 1
                    }))
                  : _createCommentVNode("", true),
                _cache[22] || (_cache[22] = _createTextVNode(" 저장 ", -1))
              ]),
              _: 1
            }, 8, ["disabled"])
          ])
        ])
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createElementVNode("input", {
      ref_key: "fileInputRef",
      ref: fileInputRef,
      type: "file",
      accept: "image/png,image/jpeg,image/jpg",
      class: "hidden-file-input",
      onChange: onFileSelected
    }, null, 544)
  ]))
}
}

})